import React, { FC } from "react";
import {
  ICreateNutritionEntryCommand,
  IUpdateNutritionEntryCommand,
  NutritionEntryDto,
  NutritionTargetDto,
} from "@growth-machine-llc/stridist-api-client";
import NutritionAccordion from "../common/NutritionAccordion";
import { BoundNutrients } from "../common/BoundNutrients";
import { Box, Collapse, Popover, Typography } from "@mui/material";
import { bindPopover, bindTrigger } from "material-ui-popup-state";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Trash, CircleChevronDown } from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as CalendarEmptyIcon } from "../../../icons/CalendarEmpty.svg";
import { DatePicker } from "../../fields/DatePicker";
import NutritionInputGroup from "../common/NutritionInputGroup";
import NutrientInput from "../common/NutrientInput";
import LoadingButton from "../../button/LoadingButton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import NutritionEntriesService from "../../../services/NutritionEntriesService";
import { CLIENT_NUTRITION_ENTRIES_QUERY_KEY } from "../ClientNutritionCard";
import { colorSystem } from "../../../theme";
import { useToastAlert } from "../../app/ToastAlert/ToastAlertProvider";
import NutritionTargetsService from "../../../services/NutritionTargetsService";
import AdvancedNutritionInput from "./AdvancedNutritionInput";

const useStyles = makeStyles((theme) => ({
  action: {
    marginBlock: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.grey[600],
    cursor: "pointer",
  },

  actionIcon: {
    width: 20,
    height: 20,
  },

  advancedContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },

  advancedColumn: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },

  button: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: "20px",
    color: theme.palette.common.white,
    backgroundColor: theme.palette.common.black,
    padding: theme.spacing(1),
    width: "100%",
    marginTop: theme.spacing(1),
  },

  calendar: {
    borderColor: "rgba(0, 0, 0, 0.87)",
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: 4,
    height: 40,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 2),
    fontSize: 16,
    fontFamily: "Montserrat",
    color: colorSystem.black,
    cursor: "pointer",
  },
}));

export interface NutrientInfo {
  value: number;
  dirty: boolean;
}

export interface EntryInfo {
  date: string;
  protein: NutrientInfo;
  carbohydrate: NutrientInfo;
  fat: NutrientInfo;
  calories: NutrientInfo;
  cholesterol: NutrientInfo;
  fiber: NutrientInfo;
  sugar: NutrientInfo;
  sodium: NutrientInfo;
  transFat: NutrientInfo;
  alcohol: NutrientInfo;
}

export interface EntryViewProps {
  expanded: boolean;
  onExpand: () => void;
  showSummaryOnExpanded: boolean;
  entries: NutritionEntryDto[];
  clientInfo: { clientId?: number; username?: string };
  onOpenOtherEntry: (otherId: number) => void;
  onAddedNewEntry: (forToday?: boolean) => void;
  onUpdatedEntry: () => void;
  entry?: NutritionEntryDto;
  target?: NutritionTargetDto;
  hardDate?: Dayjs;
}

function getEntryByDate(dateValue: Dayjs, entries: NutritionEntryDto[]) {
  const entry = entries.find((entry) => {
    const entryDate = dayjs(entry.date.toISOString());
    return entryDate.isSame(dateValue, "day");
  });
  return entry;
}

function getLatestMissingDate(dates: Dayjs[]) {
  const dateSet = new Set(dates.map((date) => date.format("YYYY-MM-DD")));
  let candidate = dayjs().startOf("day");
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const formattedCandidate = candidate.format("YYYY-MM-DD");
    if (!dateSet.has(formattedCandidate)) {
      return candidate;
    }
    candidate = candidate.subtract(1, "day");
  }
}

const CLIENT_NUTRITION_TARGET_FOR_DATE_QUERY_KEY =
  "client-nutrition-target-for-date-key";

const EntryView = React.forwardRef(
  (
    {
      expanded,
      onExpand,
      showSummaryOnExpanded,
      onOpenOtherEntry,
      onAddedNewEntry,
      onUpdatedEntry,
      clientInfo,
      entry: initEntryData,
      target: initTarget,
      entries,
      hardDate,
    }: EntryViewProps,
    ref,
  ) => {
    const isUpdateForm = !!initEntryData;
    const { clientId, username } = clientInfo;
    const queryClient = useQueryClient();
    const { showToastAlert } = useToastAlert();
    const s = useStyles();

    const [advancedExpanded, setAdvancedExpanded] =
      React.useState<boolean>(false);

    const [date, setDate] = React.useState(
      hardDate ||
        initEntryData?.date ||
        getLatestMissingDate(
          entries.map((e) => dayjs(e.date.toISOString())),
        ).startOf("day"),
    );
    const { data: targetForDate, refetch } = useQuery({
      queryKey: [
        CLIENT_NUTRITION_TARGET_FOR_DATE_QUERY_KEY,
        { clientId, date: date.format("YYYY-MM-DD") },
      ],
      queryFn: () =>
        NutritionTargetsService.getRecomendedTarget(
          username,
          date.year(),
          date.month() + 1,
          date.date(),
        ),
      enabled: !isUpdateForm && expanded && Boolean(date),
    });

    const datePickerState = usePopupState({
      variant: "popover",
      popupId: "nutrition-entry-date",
    });

    const [proteinValue, setProteinValue] = React.useState<number>(
      initEntryData?.protein || 0,
    );
    const [proteinDirty, setProteinDirty] = React.useState<boolean>(false);

    const [carbohydrateValue, setCarbohydrateValue] = React.useState<number>(
      initEntryData?.carbs || 0,
    );
    const [carbohydrateDirty, setCarbohydrateDirty] =
      React.useState<boolean>(false);

    const [fatValue, setFatValue] = React.useState<number>(
      initEntryData?.fat || 0,
    );
    const [fatDirty, setFatDirty] = React.useState<boolean>(false);

    const [caloriesValue, setCaloriesValue] = React.useState<number>(
      initEntryData?.calories || 0,
    );
    const [caloriesDirty, setCaloriesDirty] = React.useState<boolean>(false);

    const [cholesterolValue, setCholesterolValue] = React.useState<number>(
      initEntryData?.cholesterol || 0,
    );
    const [cholesterolDirty, setCholesterolDirty] =
      React.useState<boolean>(false);

    const [fiberValue, setFiberValue] = React.useState<number>(
      initEntryData?.fiber || 0,
    );
    const [fiberDirty, setFiberDirty] = React.useState<boolean>(false);

    const [sugarValue, setSugarValue] = React.useState<number>(
      initEntryData?.sugar || 0,
    );
    const [sugarDirty, setSugarDirty] = React.useState<boolean>(false);

    const [sodiumValue, setSodiumValue] = React.useState<number>(
      initEntryData?.sodium || 0,
    );
    const [sodiumDirty, setSodiumDirty] = React.useState<boolean>(false);

    const [transFatValue, setTransFatValue] = React.useState<number>(
      initEntryData?.transFat || 0,
    );
    const [transFatDirty, setTransFatDirty] = React.useState<boolean>(false);

    const [alcoholValue, setAlcoholValue] = React.useState<number>(
      initEntryData?.alcohol || 0,
    );
    const [alcoholDirty, setAlcoholDirty] = React.useState<boolean>(false);

    const handleToggleExpandAdvanced = () => {
      setAdvancedExpanded((advancedExpanded) => !advancedExpanded);
    };

    const { mutate: addNutritionEntry, isPending: addingEntry } = useMutation({
      mutationFn: NutritionEntriesService.createNutritionEntry,
      onSuccess: (data, variables) => {
        // optimistic update before refetch is done
        queryClient.setQueryData(
          [CLIENT_NUTRITION_ENTRIES_QUERY_KEY, { username }],
          (oldData: NutritionEntryDto[] | undefined) => {
            const existingData = oldData || [];

            const newNutritionEntry = NutritionEntryDto.fromJS({
              ...variables,
              id: data,
              clientId,
              lastModified: dayjs(),
            });

            return [newNutritionEntry, ...existingData];
          },
        );
        // trigger refetch
        queryClient.invalidateQueries({
          queryKey: [CLIENT_NUTRITION_ENTRIES_QUERY_KEY],
        });

        onAddedNewEntry(variables.date?.isSame(dayjs(), "day"));

        showToastAlert("success", {
          message: "Nutrition entry sync successfully",
        });
      },
    });

    const { mutate: updateNutritionEntry, isPending: updatingEntry } =
      useMutation({
        mutationFn: NutritionEntriesService.updateNutritionEntry,
        onSuccess: (_, variables) => {
          // optimistic update before refetch is done
          queryClient.setQueryData(
            [CLIENT_NUTRITION_ENTRIES_QUERY_KEY, { username }],
            (oldData: NutritionEntryDto[] | undefined) => {
              const existingData = oldData || [];

              const oldDataEntry =
                existingData.find((entry) => entry.id === variables.id) || {};

              const updatedEntry = NutritionEntryDto.fromJS({
                ...oldDataEntry,
                ...variables,
                clientId,
                lastModified: dayjs(),
              });

              return oldData?.map((entry) =>
                entry.id === variables.id ? updatedEntry : entry,
              );
            },
          );
          // trigger refetch
          queryClient.invalidateQueries({
            queryKey: [CLIENT_NUTRITION_ENTRIES_QUERY_KEY],
          });

          onUpdatedEntry();

          showToastAlert("success", {
            message: "Nutrition entry sync successfully",
          });
        },
      });

    const { mutate: deleteNutritionEntry, isPending: deletingEntry } =
      useMutation({
        mutationFn: NutritionEntriesService.deleteNutritionEntry,
        onSuccess: (_, variables) => {
          queryClient.setQueryData(
            [CLIENT_NUTRITION_ENTRIES_QUERY_KEY, { username }],
            (oldData: NutritionEntryDto[] | undefined) => {
              return oldData.filter((entry) => entry.id !== variables);
            },
          );

          showToastAlert("success", {
            message: "Nutrition entry deleted",
          });
        },
      });

    const handleDateChange = (value) => {
      const newDate = value as Dayjs;
      const entryByDate = getEntryByDate(newDate, entries);
      if (entryByDate && entryByDate.id !== initEntryData?.id) {
        onOpenOtherEntry(entryByDate.id);
      } else {
        setDate(newDate.startOf("day"));
        datePickerState.close();
      }
    };

    const handleClickSave = () => {
      const sharedPayload = {
        calories: caloriesValue,
        protein: proteinValue,
        carbs: carbohydrateValue,
        fat: fatValue,

        cholesterol: cholesterolValue,
        fiber: fiberValue,
        sugar: sugarValue,
        sodium: sodiumValue,
        transFat: transFatValue,
        alcohol: alcoholValue,
      };
      if (isUpdateForm) {
        const payload = {
          id: initEntryData?.id,
          ...sharedPayload,
        } as IUpdateNutritionEntryCommand;
        updateNutritionEntry(payload);
      } else {
        const payload = {
          date,
          username: username,
          ...sharedPayload,
        } as ICreateNutritionEntryCommand;
        addNutritionEntry(payload);
      }
    };

    const handleClickDelete = () => {
      if (initEntryData) {
        deleteNutritionEntry(initEntryData.id);
      }
    };

    React.useEffect(() => {
      const listener = (event: KeyboardEvent) => {
        if (event.key === "Enter" && expanded) {
          handleClickSave();
        }
      };

      if (expanded) {
        window.addEventListener("keydown", listener);
      }

      return () => {
        window.removeEventListener("keydown", listener);
      };
    }, [expanded, handleClickSave]);

    const isPending = addingEntry || updatingEntry || deletingEntry;

    const target =
      !isUpdateForm && expanded && targetForDate ? targetForDate : initTarget;

    return (
      <NutritionAccordion
        ref={ref}
        expanded={expanded}
        onChange={() => onExpand()}
        calories={caloriesValue}
        targetCalories={target?.calories}
        protein={proteinValue}
        carbs={carbohydrateValue}
        fat={fatValue}
        title={date.format("MMM D, YYYY")}
        pencilOff={!hardDate}
        showSummaryOnExpanded={showSummaryOnExpanded}
        secondaryAction={
          isUpdateForm && !hardDate
            ? {
                name: "Delete entry",
                onClick: handleClickDelete,
                icon: <Trash />,
              }
            : undefined
        }
      >
        {!isUpdateForm && !hardDate && (
          <NutritionInputGroup label="Date">
            <Box {...bindTrigger(datePickerState)} className={s.calendar}>
              <span>{date.format("MMM DD YYYY")}</span>
              <CalendarEmptyIcon />
            </Box>
            <Popover
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              sx={{
                zIndex: 1450,
              }}
              {...bindPopover(datePickerState)}
            >
              <DatePicker
                value={date.toDate()}
                onChange={handleDateChange}
                highlightDays={entries.map(({ date }) =>
                  dayjs(date.toISOString()).toDate(),
                )}
              />
            </Popover>
          </NutritionInputGroup>
        )}
        <Box>
          <BoundNutrients
            protein={{
              targetValue: target?.protein,
              value: proteinValue,
              dirty: proteinDirty,
              setValue: setProteinValue,
              setDirty: setProteinDirty,
            }}
            carbohydrate={{
              targetValue: target?.carbs,
              value: carbohydrateValue,
              dirty: carbohydrateDirty,
              setValue: setCarbohydrateValue,
              setDirty: setCarbohydrateDirty,
            }}
            fat={{
              targetValue: target?.fat,
              value: fatValue,
              dirty: fatDirty,
              setValue: setFatValue,
              setDirty: setFatDirty,
            }}
            calories={{
              targetValue: target?.calories,
              value: caloriesValue,
              dirty: caloriesDirty,
              setValue: setCaloriesValue,
              setDirty: setCaloriesDirty,
            }}
            variant="entry"
          />
        </Box>
        <Box className={s.action} onClick={handleToggleExpandAdvanced}>
          <CircleChevronDown
            className={s.actionIcon}
            style={{
              transform: advancedExpanded ? "rotate(-180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          />
          <Typography>Advanced nutrition</Typography>
        </Box>
        <Collapse in={advancedExpanded} timeout="auto" unmountOnExit>
          <Box className={s.advancedContainer}>
            <Box className={s.advancedColumn}>
              <NutritionInputGroup label="Cholesterol">
                <AdvancedNutritionInput
                  value={cholesterolValue}
                  dirty={cholesterolDirty}
                  onChange={setCholesterolValue}
                />
              </NutritionInputGroup>

              <NutritionInputGroup label="Sugar">
                <AdvancedNutritionInput
                  value={sugarValue}
                  dirty={sugarDirty}
                  onChange={setSugarValue}
                />
              </NutritionInputGroup>

              <NutritionInputGroup label="Trans fat">
                <AdvancedNutritionInput
                  value={transFatValue}
                  dirty={transFatDirty}
                  onChange={setTransFatValue}
                />
              </NutritionInputGroup>
            </Box>
            <Box className={s.advancedColumn}>
              <NutritionInputGroup label="Fiber">
                <AdvancedNutritionInput
                  value={fiberValue}
                  dirty={fiberDirty}
                  onChange={setFiberValue}
                />
              </NutritionInputGroup>

              <NutritionInputGroup label="Sodium">
                <AdvancedNutritionInput
                  value={sodiumValue}
                  dirty={sodiumDirty}
                  onChange={setSodiumValue}
                />
              </NutritionInputGroup>

              <NutritionInputGroup label="Alcohol">
                <AdvancedNutritionInput
                  value={alcoholValue}
                  dirty={alcoholDirty}
                  onChange={setAlcoholValue}
                />
              </NutritionInputGroup>
            </Box>
          </Box>
        </Collapse>
        <LoadingButton
          className={s.button}
          variant="contained"
          children={(isUpdateForm ? "Update" : "Save") + " nutrition entry"}
          onClick={handleClickSave}
          loading={isPending}
        />
      </NutritionAccordion>
    );
  },
);

export default EntryView;
